import { emailClient, type EmailMessage } from "@email/emailClient";
import { emailKeys } from "@email/emailQueries";
import { Button, FieldError } from "@scribenote/brian-design-system";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export const EmailAssociation = ({ message }: { message: EmailMessage }) => {
  const cache = useQueryClient();
  const link = useMutation({
    mutationFn: ({
      client,
      patient,
    }: {
      client: string | null;
      patient: string | null;
    }) => emailClient.linkMessage(message.uuid, client, patient),
    meta: { handled: true },
    onSuccess: () => cache.invalidateQueries({ queryKey: emailKeys.all }),
  });
  const association = message.association;
  if (!association) return null;
  return (
    <div className="mt-3 space-y-2 border-y border-rule py-3 text-sm">
      {association.client ? (
        <>
          <p>
            Client: <strong>{association.client.name}</strong>
          </p>
          <label className="flex flex-col gap-1">
            Patient
            <select
              className="rounded-paper border border-rule bg-card p-2"
              value={association.patient?.uuid ?? ""}
              disabled={link.isPending}
              onChange={(event) =>
                link.mutate({
                  client: association.client!.uuid,
                  patient: event.target.value || null,
                })
              }
            >
              <option value="">Client only — no patient linked</option>
              {(association.patients ?? []).map((patient) => (
                <option key={patient.uuid} value={patient.uuid}>
                  {patient.name}
                </option>
              ))}
            </select>
          </label>
          <Button
            size="sm"
            variant="ghost"
            disabled={link.isPending}
            onClick={() => link.mutate({ client: null, patient: null })}
          >
            Unlink client
          </Button>
        </>
      ) : (association.candidates?.length ?? 0) > 0 ? (
        <>
          <p>Choose a client matching this sender:</p>
          <div className="flex flex-wrap gap-2">
            {(association.candidates ?? []).map((client) => (
              <Button
                key={client.uuid}
                size="sm"
                variant="outline"
                disabled={link.isPending}
                onClick={() =>
                  link.mutate({ client: client.uuid, patient: null })
                }
              >
                {client.name}
              </Button>
            ))}
          </div>
        </>
      ) : (
        <p className="text-annotation">No client associated.</p>
      )}
      {link.isError && (
        <FieldError>
          Could not save the association. Refresh and try again.
        </FieldError>
      )}
    </div>
  );
};
